import type { ColumnFiltersAction } from "./actions";
import { applyFilterAction } from "./applyFilterAction";
import type {
  ColumnFilter,
  ColumnFilterMap,
  FilterStage,
  LiveColumnFilters,
} from "./state";

/**
 * Structural equality over a single stage.
 *
 * Exported for `ColumnFilterPopover`, which uses it to recognize the stage its
 * text buffers are synced to: a stage that compares equal is the row's own
 * round trip, and anything else is a write from elsewhere that re-seeds the
 * buffers.
 *
 * `value2` is optional and compared exactly, so an absent second bound and an
 * empty one are unequal. Search reads the two the same way
 * (`LogSearchEngine.safeParseFloat`), but a false match is the costlier error
 * for both callers: it would let the popover keep a draft typed against a
 * different stage, and let `commitStep` drop a real edit.
 */
export function filterStageEqual(a: FilterStage, b: FilterStage): boolean {
  return (
    a.value === b.value && a.condition === b.condition && a.value2 === b.value2
  );
}

function stagesEqual(a: ColumnFilter["stages"], b: ColumnFilter["stages"]) {
  return (
    a.length === b.length &&
    a.every((stage, i) => filterStageEqual(stage, b[i]))
  );
}

function filtersEntryEqual(a: ColumnFilter, b: ColumnFilter) {
  return a.combinator === b.combinator && stagesEqual(a.stages, b.stages);
}

/**
 * Structural equality over filter maps. Compares every stage of every entry;
 * the map holds at most one entry per filterable column, so the walk is
 * bounded by the panel's column count.
 */
export function columnFiltersEqual(
  a: ColumnFilterMap,
  b: ColumnFilterMap,
): boolean {
  if (a === b) {
    return true;
  }
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) {
    return false;
  }
  return aKeys.every(
    (key) => Object.hasOwn(b, key) && filtersEntryEqual(a[key], b[key]),
  );
}

export interface CommitStepResult {
  /** The map to commit to `panel.config.columnFilters`. */
  filters: ColumnFilterMap;
  changed: boolean;
}

/**
 * One step of the config-commit adapter: apply a filter action to the filters
 * currently in effect and decide whether the result warrants a
 * `setLogPanelConfig` dispatch.
 *
 * `changed` is content-aware, not identity-based: `applyFilterAction` allocates
 * a fresh map for every action it does not reject outright, so an identity
 * check would let redundant commits (a debounce trailing edge after a blur
 * flush, a re-typed identical value) trigger workspace-wide re-renders for
 * nothing.
 *
 * Every action carries a whole edit — `SetColumnStagesAction` publishes a
 * column's entire stage list rather than one append per stage — so a dispatch
 * is always a single step against the filters derived from config. Keep it that
 * way: a handler that dispatched twice in a tick would apply the second action
 * to a live map that has not seen the first.
 */
export function commitStep(
  live: LiveColumnFilters,
  action: ColumnFiltersAction,
): CommitStepResult {
  const filters = applyFilterAction(live.filters, action, live.columns);
  return { filters, changed: !columnFiltersEqual(live.filters, filters) };
}
